import React, { useState, useEffect, useCallback } from 'react';
import { Button, message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, type OptionalString, formatTimeDisplay } from '../hooks/useStepDetailRender';
import EditToolbar, { useEditToolbar } from '../../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../../graphql/queries/getTmsTaskDetail';
import { SAVE_TMS_UNLOADING_DETAIL_INFO, type SaveTmsUnloadingDetailInfoInput } from '../../../graphql/mutations/taskMutations';
import { TmsOperationTypeEnum, WaypointTypeEnum } from '../../../../../../enums/TmsEnum';
import ContainerInfoTable, { type ContainerInfoRow } from './ContainerInfoTable';
import { useContainerInfoRender, UNIT_TO_CONTAINER_TYPE } from '../hooks/useContainerInfoRender';

interface InboundInfoSectionProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    onOpenUnloadingDetailDrawer?: (waypointId: number) => void;
    task?: TmsTaskDetailType;
}

const InboundInfoSection: React.FC<InboundInfoSectionProps> = ({ waypoint, stepDetailAllowEdit, onOpenUnloadingDetailDrawer, task }) => {
    const [unloadingTimeDisplay, setUnloadingTimeDisplay] = useState<OptionalString>(
        formatTimeDisplay(waypoint.unLoadingTime)
    );
    const [unloadingTimeTimezone] = useState<OptionalString>(waypoint.unLoadingTimeTimezone);
    const [interiorPhotoUrls, setInteriorPhotoUrls] = useState<string[]>(
        waypoint.bolPhotosUrlsJson || []
    );

    const [unloadingTimeDraft, setUnloadingTimeDraft] = useState<Dayjs | null>(
        waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null
    );
    const [photosDraft, setPhotosDraft] = useState<string[]>(waypoint.bolPhotosJson || []);
    const [remarkDraft, setRemarkDraft] = useState<string>(waypoint.bolRemark || '');
    const [containerRowsDraft, setContainerRowsDraft] = useState<ContainerInfoRow[]>([]);

    const taskWaypointId = Number(waypoint.id);
    /** 卸车凭证(BOL)照片仅终点途经点需要；途经站卸车无 BOL */
    const isDestinationWaypoint = waypoint.waypointType === WaypointTypeEnum.Destination;
    const { containerInfoRows, containerDestinationOptions, parcelCount } = useContainerInfoRender({
        waypoint,
        task,
        containerSource: TmsOperationTypeEnum.Unloading,
    });

    const buildDefaultUnloadingRows = useCallback((): ContainerInfoRow[] => {
        if (containerDestinationOptions.length === 0) return [];
        // 优先使用当前站点（waypointSortingCode）对应的目的地；找不到时退回第一个
        const defaultOption =
            containerDestinationOptions.find(
                (opt) =>
                    opt.label === waypoint.waypointSortingCode ||
                    opt.value === String(waypoint.waypointId)
            ) ?? { value: '-', label: '-' };
        return [
            {
                key: `unloading-default-${waypoint.id}`,
                destination: defaultOption.value,
                quantity: 1,
                unit: 'PALLET_BOX',
            },
        ];
    }, [containerDestinationOptions, waypoint.id, waypoint.waypointId, waypoint.waypointSortingCode]);

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setUnloadingTimeDraft(waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null);
            setPhotosDraft(waypoint.bolPhotosJson || []);
            setRemarkDraft(waypoint.bolRemark || '');
            const initialRows =
                containerInfoRows.length > 0 ? containerInfoRows : buildDefaultUnloadingRows();
            setContainerRowsDraft(initialRows);
        }, [waypoint.unLoadingTime, waypoint.bolPhotosJson, waypoint.bolRemark, containerInfoRows, buildDefaultUnloadingRows]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [saveUnloadingDetailMutation] = useMutation(SAVE_TMS_UNLOADING_DETAIL_INFO);

    const unloadingTimeLabel = t('tms.task.drawer.unloadingTime');
    const photoLabel = t('tms.task.drawer.unloadingBolPhotos');
    const unloadingInfoLabel = t('tms.task.drawer.unloadingInfo');
    const parcelCountLabel = t('tms.task.drawer.unloadingInfoParcelCount');
    const containerInfoLabel = t('tms.task.drawer.unloadingInfoContainerInfo');
    const remarkLabel = t('common.remark');

    const { renderTimeRow, renderPhotoRow, renderRemarkRow } = useStepDetailRender({ isEditing });

    useEffect(() => {
        setUnloadingTimeDisplay(formatTimeDisplay(waypoint.unLoadingTime));
        setUnloadingTimeDraft(waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null);
    }, [waypoint.unLoadingTime]);

    useEffect(() => {
        setInteriorPhotoUrls(waypoint.bolPhotosUrlsJson || []);
    }, [waypoint.bolPhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setPhotosDraft(waypoint.bolPhotosJson || []);
            setRemarkDraft(waypoint.bolRemark || '');
        }
    }, [waypoint.bolPhotosJson, waypoint.bolRemark, isEditing]);

    const validateSave = () => {
        if (!unloadingTimeDraft) {
            message.error(t('tms.task.drawer.unloadingTimeRequired'));
            return false;
        }
        if (containerRowsDraft.length === 0) {
            message.error(t('tms.task.drawer.unloadingContainerRequired'));
            return false;
        }
        // 每一行的目的地、数量、单位都必填（目的地在卸车时只读，但必须有值）
        for (const row of containerRowsDraft) {
            if (!row.destination || !row.destination.trim()) {
                message.error(t('tms.task.drawer.containerDestinationRequired'));
                return false;
            }
            if (row.quantity == null || row.quantity <= 0) {
                message.error(t('tms.task.drawer.containerQuantityRequired'));
                return false;
            }
            if (!row.unit) {
                message.error(t('tms.task.drawer.containerUnitRequired'));
                return false;
            }
        }
        if (isDestinationWaypoint && photosDraft.length === 0) {
            message.error(t('tms.task.drawer.bolPhotosRequired'));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateSave()) return;

        const unloadingLocalTime = unloadingTimeDraft ? unloadingTimeDraft.format('YYYY-MM-DD HH:mm') : null;
        const items = containerRowsDraft.map((row) => {
            const sortingCode =
                row.destination?.trim() != null
                    ? (containerDestinationOptions.find((opt) => opt.value === row.destination)?.label ?? null)
                    : null;
            return {
                containerType: row.unit ? UNIT_TO_CONTAINER_TYPE[row.unit] : undefined,
                containerCount: row.quantity,
                sortingCode,
            };
        });
        const input: SaveTmsUnloadingDetailInfoInput = {
            taskId: waypoint.taskId,
            taskWaypointId: Number(waypoint.id),
            items,
            bolRemark: remarkDraft || null,
            // 仅终点提交 BOL 照片；途经站不传，避免误清空
            bolPhotosJson: isDestinationWaypoint ? photosDraft : (waypoint.bolPhotosJson ?? null),
            unloadingLocalTime,
        };
        const refetchQueries = [{ query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } }];

        try {
            await saveUnloadingDetailMutation({
                variables: { input },
                refetchQueries,
                awaitRefetchQueries: true,
            });

            setUnloadingTimeDisplay(unloadingTimeDraft ? unloadingTimeDraft.format('YYYY-MM-DD HH:mm') : null);
            message.success(t('tms.task.drawer.saveSuccess'));
            exitEdit();
        } catch (error: any) {
            console.error('Failed to save unloading detail:', error);
            message.error(error?.message || t('tms.task.drawer.saveFailed'));
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="mt-1 space-y-3">
                {/* 卸车时间 */}
                {renderTimeRow(
                    unloadingTimeLabel,
                    unloadingTimeDisplay,
                    unloadingTimeTimezone,
                    unloadingTimeDraft,
                    setUnloadingTimeDraft,
                    true,
                    waypoint.unLoadingTimeTimezone
                )}
                {/* 入库运单数量 */}
                <div className="flex items-center gap-3 min-h-[28px] pb-3">
                    {onOpenUnloadingDetailDrawer ? (
                        <Button
                            type="link"
                            className="p-0 h-auto text-left"
                            onClick={() => onOpenUnloadingDetailDrawer(taskWaypointId)}
                        >
                            <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                            <span className="ml-1">
                                {parcelCount === null ? String(t('tms.task.drawer.loading')) : parcelCount}
                            </span>
                        </Button>
                    ) : (
                        <>
                            <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                            <span className="text-gray-700">{parcelCount === null ? String(t('tms.task.drawer.loading')) : parcelCount}</span>
                        </>
                    )}
                </div>
                {/* 卸车信息 */}
                <div className="border-b border-gray-100">
                    <div className="text-sm text-gray-500 pb-3">{unloadingInfoLabel}:</div>
                    <div className="mt-2 pl-4 space-y-2">
                        <div className="flex gap-1 items-start">
                            <div className="mr-3 text-sm text-gray-500">{containerInfoLabel}:</div>
                            <div className="flex-1 max-w-[600px]">
                                <ContainerInfoTable
                                    dataSource={!isEditing ? containerInfoRows : containerRowsDraft}
                                    destinationOptions={containerDestinationOptions}
                                    readOnly={!isEditing}
                                    unloading
                                    onChange={isEditing ? setContainerRowsDraft : undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* 卸车凭证(BOL照片)：仅终点途经点展示 */}
                {isDestinationWaypoint &&
                    renderPhotoRow(photoLabel, interiorPhotoUrls, photosDraft, setPhotosDraft, interiorPhotoUrls)}
                {renderRemarkRow(remarkLabel, waypoint.bolRemark, remarkDraft, setRemarkDraft)}
            </div>
        </div>
    );
};

export default InboundInfoSection;
